import gql from 'graphql-tag'
import { expandTagFetches } from '@/helpers/foam-tags'

export default config => {
  const { client } = config

  function resolveTagOperator (tags, tagOperator) {
    if (typeof tagOperator === 'boolean') return tagOperator
    // Multiple tags mean "match all" (AND). Single-tag queries are unchanged.
    return Array.isArray(tags) && tags.length > 1
  }

  async function queryAggregate ({
    start_date,
    end_date,
    tags,
    tag_operator,
    customer_id
  }) {
    const tagOperator = resolveTagOperator(tags, tag_operator)

    var query = gql`
      query (
        $filter_tag: jsonb
        $start_date: timestamp
        $end_date: timestamp
        $tag_operator: Boolean
      ) {
        stats: item_tag_stats_per_week_aggregate(
          args: {
            filter_tag: $filter_tag
            start_date: $start_date
            end_date: $end_date
            tag_operator: $tag_operator
          }
        ) {
          aggregate {
            sum {
              quantity
              sales_revenue
            }
          }
        }
      }
    `

    var variables = {
      start_date,
      end_date,
      filter_tag: tags,
      tag_operator: tagOperator
    }

    if (customer_id !== null) {
      query = gql`
        query (
          $filter_tag: jsonb
          $start_date: timestamp
          $end_date: timestamp
          $customer_id: uuid
          $tag_operator: Boolean
        ) {
          stats: item_tag_stats_per_customer_per_week(
            args: {
              filter_tag: $filter_tag
              start_date: $start_date
              end_date: $end_date
              filter_customer_id: $customer_id
              tag_operator: $tag_operator
            }
          ) {
            month
            quantity
            sales_revenue
            tags
            week
            year
            customer_id
          }
        }
      `
      variables = { ...variables, customer_id }
    }

    const { data } = await client.query({
      query,
      variables
    })

    if (customer_id !== null) return data.stats
    return data.stats.aggregate.sum
  }

  async function querySeries ({
    start_date,
    end_date,
    tags,
    tag_operator,
    customer_id
  }) {
    const tagOperator = resolveTagOperator(tags, tag_operator)

    let query
    let variables = {
      start_date,
      end_date,
      filter_tag: tags,
      tag_operator: tagOperator
    }

    if (customer_id !== null) {
      query = gql`
        query (
          $filter_tag: jsonb
          $start_date: timestamp
          $end_date: timestamp
          $customer_id: uuid
          $tag_operator: Boolean
        ) {
          stats: item_tag_stats_per_customer_per_week(
            args: {
              filter_tag: $filter_tag
              start_date: $start_date
              end_date: $end_date
              filter_customer_id: $customer_id
              tag_operator: $tag_operator
            }
          ) {
            month
            quantity
            sales_revenue
            tags
            week
            year
            customer_id
          }
        }
      `
      variables = { ...variables, customer_id }
    } else {
      query = gql`
        query (
          $filter_tag: jsonb
          $start_date: timestamp
          $end_date: timestamp
          $tag_operator: Boolean
        ) {
          stats: item_tag_stats_per_week(
            args: {
              filter_tag: $filter_tag
              start_date: $start_date
              end_date: $end_date
              tag_operator: $tag_operator
            }
          ) {
            month
            quantity
            sales_revenue
            tags
            week
            year
          }
        }
      `
    }

    const { data } = await client.query({
      query,
      variables
    })

    return data.stats || []
  }

  function sumAggregateResults (parts) {
    return parts.reduce(
      (acc, part) => {
        if (Array.isArray(part)) {
          // Customer-scoped fetch returns weekly rows — sum like quantity-stat callers expect
          return {
            quantity: (acc.quantity || 0) + part.reduce((s, r) => s + (Number(r.quantity) || 0), 0),
            sales_revenue:
              (acc.sales_revenue || 0) +
              part.reduce((s, r) => s + (Number(r.sales_revenue) || 0), 0)
          }
        }
        return {
          quantity: (acc.quantity || 0) + (Number(part && part.quantity) || 0),
          sales_revenue:
            (acc.sales_revenue || 0) +
            (Number(part && part.sales_revenue) || 0)
        }
      },
      { quantity: 0, sales_revenue: 0 }
    )
  }

  function mergeSeriesRows (parts) {
    const byKey = new Map()
    parts.forEach(rows => {
      const list = rows || []
      list.forEach(row => {
        const key = [
          row.year,
          row.month,
          row.week,
          row.customer_id || ''
        ].join('|')
        const prev = byKey.get(key)
        if (!prev) {
          byKey.set(key, { ...row })
          return
        }
        prev.quantity = (Number(prev.quantity) || 0) + (Number(row.quantity) || 0)
        prev.sales_revenue =
          (Number(prev.sales_revenue) || 0) + (Number(row.sales_revenue) || 0)
      })
    })
    return Array.from(byKey.values())
  }

  return {
    fetch: async payload => {
      const {
        start_date,
        end_date,
        tags,
        customer_id = null,
        tag_operator = null
      } = payload
      if (!start_date) throw new Error('start_date is undefined')
      if (!end_date) throw new Error('end_date is undefined')
      if (!tags) throw new Error('tags is undefined')

      const fetches = expandTagFetches(tags, tag_operator)

      if (fetches.length === 1) {
        const result = await queryAggregate({
          start_date,
          end_date,
          tags: fetches[0].tags,
          tag_operator: fetches[0].tag_operator,
          customer_id
        })
        // Preserve prior shape: customer fetch returns row arrays
        if (customer_id !== null) return result
        return result
      }

      const parts = await Promise.all(
        fetches.map(f =>
          queryAggregate({
            start_date,
            end_date,
            tags: f.tags,
            tag_operator: f.tag_operator,
            customer_id
          })
        )
      )

      if (customer_id !== null) {
        // Merge weekly rows from each alias fetch
        return mergeSeriesRows(parts)
      }

      return sumAggregateResults(parts)
    },

    /** Weekly rows for stacked / time-series charts (admin + customer). */
    fetchSeries: async payload => {
      const {
        start_date,
        end_date,
        tags,
        customer_id = null,
        tag_operator = null
      } = payload
      if (!start_date) throw new Error('start_date is undefined')
      if (!end_date) throw new Error('end_date is undefined')
      if (!tags) throw new Error('tags is undefined')

      const fetches = expandTagFetches(tags, tag_operator)

      if (fetches.length === 1) {
        return querySeries({
          start_date,
          end_date,
          tags: fetches[0].tags,
          tag_operator: fetches[0].tag_operator,
          customer_id
        })
      }

      const parts = await Promise.all(
        fetches.map(f =>
          querySeries({
            start_date,
            end_date,
            tags: f.tags,
            tag_operator: f.tag_operator,
            customer_id
          })
        )
      )

      return mergeSeriesRows(parts)
    }
  }
}
